import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { dropdownValues } from '@interfaces/common.model';
import { allowedKeys } from '@shared/config/allowedKeys.config';
import { ErrorMessages } from '@constants/constants';

export function validateNumberInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  if (!/^\d*$/.test(value)) {
    input.value = value.replace(/\D/g, '');
    const formControlName = input.getAttribute('formControlName');
    if (formControlName) {
      const formGroup = input.closest('form')?.['formGroup'];
      formGroup?.get(formControlName)?.setValue(input.value);
    }
  }
}

export function noOnlyZerosValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const isOnlyZeros = /^0+$/.test(control.value);
    return isOnlyZeros ? { onlyZeros: true } : null;
  };
}

// Custom validator to check if relevant experience is greater than overall experience
export function experienceValidator(
  overallExpControlName: string,
  relExpControlName: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control.parent;
    if (!formGroup) {
      return null;
    }
    const overallExpControl = formGroup.get(overallExpControlName);
    const relExpControl = formGroup.get(relExpControlName);
    if (
      overallExpControl &&
      overallExpControl.value &&
      relExpControl &&
      relExpControl.value &&
      parseFloat(relExpControl.value) > parseFloat(overallExpControl.value)
    ) {
      return { relevantExperienceExceeds: true };
    }
    return null;
  };
}

export function maxOverallExperienceValidator(maxYears: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = parseFloat(control.value);
    if ((value && value > maxYears) || value < 0) {
      return { maxOverallExperience: true };
    }
    return null;
  };
}

export function maxLengthValidator(maxLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control?.value;
    if (value && value.toString().length > maxLength) {
      return {
        maxLength: {
          requiredLength: maxLength,
          actualLength: value.toString().length,
        },
      };
    }
    return null;
  };
}

export function sortDropdownValues(values: dropdownValues[]): dropdownValues[] {
  return values?.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
}

export function restrictInvalidKeyPress(event: KeyboardEvent) {
  const key = event.key;
  if (!allowedKeys.includes(key)) {
    event.preventDefault();
  }
}

// Custom validator to check if a file is selected or already uploaded
export function uploadedFileValidator(
  selectedFile: string | null,
): ValidatorFn {
  return (_control: AbstractControl): { [key: string]: any } | null => {
    return selectedFile ? null : { required: true };
  };
}

export function getFormControlError(
  control: AbstractControl | null,
  fieldName?: string,
): string {
  if (control?.errors) {
    if (control.errors['required']) {
      return ErrorMessages.FieldCannotBeBlank;
    }
    if (control.errors['invalidDateFormat']) {
      return 'Invalid date format';
    }
    if (control.errors['invalidDate']) {
      return 'Start date should be at least 3 days from today';
    }
    if (control.errors['minlength']) {
      const minLength = control.errors['minlength'].requiredLength;
      return `This field must be at least ${minLength} characters long.`;
    }
    if (control.errors['min']) {
      const min = control.errors['min'].min;
      return `Minimum value should be ${min}.`;
    }
    if (control.errors['email']) {
      return `Invalid email format.`;
    }
    if (control.errors['pattern']) {
      if (fieldName === 'url') {
        return 'Invalid URL format.';
      } else {
        return `Numbers only`;
      }
    }
  }
  return '';
}
